import { useEffect, useState, useRef, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { EuiFieldSearch, EuiFilterButton, EuiIcon } from "@elastic/eui";
import FDFilterDropdownFooter from "components/FDFilterDropdown/components/FDFilterDropdownFooter";
import globalFilterTypes from "reduxStore/globalFilter/globalFilter.types";
import { ExportHiddenClass } from "Consts";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";
import SideFilterDropdown from "./SideFilterDropdown";
import { filterUniqueIds } from "../../TopFilter/TopFilterUtils";
import Popover from "components/Popover/Popover";
import { getDropdownData, getDateBasedDropdownData } from "../FDFilterDropdownService";

const SideFilter = ({ Filters, selectedFilters }: any) => {
  const dispatch = useDispatch();
  const textSearchDelayTimer = useRef<any>(null);

  const [isOpen, setOpen] = useState<boolean>(false);
  const [isApply, setApply] = useState<boolean>(false);
  const [openAccordions, setOpenAccordions] = useState({});
  const [value, setValue] = useState<string>("");
  const [filterText, setFilterText] = useState<string>("");
  const [allActiveFilters, setAllActiveFilters] = useState<any>({});
  const [filterOptions, setFilterOptions] = useState<any>({});

  const { clientId, DatePickerParams, GlobalTopFilterObject, GlobalActiveFilters } = useSelector(
    globalFilterSelector,
    shallowEqual
  );

  const _FILTERS = Filters?.filter((i: any) => !i?.options);
  const filterFields = Filters?.map((i: any) => i?.field);

  const isLimitError =
    Object?.values(allActiveFilters)
      .map((i: any) => Object?.keys(i))
      .flat()?.length > 200;

  const mergedTopFilters = useMemo(
    () => ({
      ...GlobalTopFilterObject,
      ...GlobalActiveFilters,
    }),
    [GlobalTopFilterObject, GlobalActiveFilters]
  );

  const rules = ["jobs_categories", "jobs_list", "blocker_ids_v2", "deadlock_groups_v2"];

  const toggleFiltersNav = () => setOpen((v) => !v);

  useEffect(() => {
    return () => {
      clearFilterText();
      setAllActiveFilters({});
    };
  }, []);

  useEffect(() => {
    if (mergedTopFilters?.hosts?.length > 0 && !filterFields?.includes("hosts")) {
      dispatch({
        type: globalFilterTypes.DROP_TOP_FILTER_HOSTS,
      });
      return;
    }

    let cancelled = false;

    async function load() {
      const result: any = {};

      const promises = _FILTERS
        .filter((i: any) => !rules.includes(i.fdType))
        .map(
          (filter: any) =>
            new Promise<void>((resolve) => {
              getDropdownData({
                clientId,
                fdType: filter.fdType,
                GlobalTopFilterObject: mergedTopFilters,
                setItems: (options) => {
                  result[filter.name] = options;
                  resolve();
                },
                setLoading: () => {},
              });
            })
        );

      await Promise.all(promises);

      if (!cancelled) {
        setFilterOptions((prev: any) => ({ ...prev, ...result }));
      }
    }

    if (_FILTERS?.length) load();
    return () => {
      cancelled = true;
    };
  }, [
    clientId,
    mergedTopFilters.hosts,
    mergedTopFilters.servername,
    mergedTopFilters.databasename,
    mergedTopFilters.tag,
  ]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const result: any = {};

      const promises = _FILTERS
        .filter((i: any) => rules.includes(i.fdType))
        .map(
          (filter: any) =>
            new Promise<void>((resolve) => {
              getDateBasedDropdownData({
                clientId,
                fdType: filter.fdType,
                DatePickerParams,
                GlobalTopFilterObject: mergedTopFilters,
                setItems: (options) => {
                  result[filter.name] = options;
                  resolve();
                },
                setLoading: () => {},
              });
            })
        );

      if (!promises.length) return;
      await Promise.all(promises);

      if (!cancelled) {
        setFilterOptions((prev: any) => ({ ...prev, ...result }));
      }
    }

    if (_FILTERS?.length) load();
    return () => {
      cancelled = true;
    };
  }, [clientId, DatePickerParams, mergedTopFilters.tag]);

  useEffect(() => () => clearTimeout(textSearchDelayTimer.current), []);

  const clearFilterText = () => {
    setValue("");
    setFilterText("");
    setOpenAccordions({});
  };

  const filtersMapping = Filters.map((i: any) => ({
    ...i,
    options: i.options || filterOptions[i.name],
  }));

  const filtersComponent = (
    <div
      className={ExportHiddenClass}
      style={{ backgroundColor: "var(--card-background)", borderRadius: "var(--box-radius)" }}>
      <EuiFieldSearch
        isClearable
        placeholder="Search"
        value={value}
        onChange={(e) => {
          const val = e.target.value;

          setValue(val);
          clearTimeout(textSearchDelayTimer.current);

          if (val === "") {
            clearFilterText();
            return;
          }

          textSearchDelayTimer.current = setTimeout(() => {
            if (val.length > 1) setFilterText(val);
          }, 400);

          if (!Object.keys(openAccordions).length) {
            const expanded: any = {};
            Filters.forEach((i: any) => (expanded[i.name] = true));
            setOpenAccordions(expanded);
          }
        }}
        style={{
          borderBottom: ".6px solid var(--border-color)",
          boxShadow: "none",
          backgroundColor: "var(--card-background)",
        }}
      />

      <div
        style={
          value.length ? { overflowY: "scroll", scrollbarWidth: "thin", maxHeight: "80vh" } : {}
        }>
        {filtersMapping.map((filter: any, index: number) => (
          <span key={filter.name + "_" + index}>
            <SideFilterDropdown
              AllowExclude={filter.allowExclude}
              Field={filter.field}
              ForceSelectedIndex={filter.forceSelectedIndex}
              ForceSelectedValue={filter.forceSelectedValue}
              ID={filterUniqueIds[index]}
              Items={filter.options}
              MaxSelectedItems={filter.maxSelectedItems}
              Name={filter.selected || filter.name}
              OnClick={filter?.onclick}
              OnMaxSelection={filter.onMaxSelection}
              SingleSelection={filter.singleSelection == 1}
              onRuleClick={filter?.onRuleClick}
              filterList={Filters}
              isApply={isApply}
              setApply={setApply}
              openAccordions={openAccordions}
              setOpenAccordions={setOpenAccordions}
              filterText={filterText}
              clearFilterText={clearFilterText}
              filtersCount={Filters?.length}
              GlobalTopFilterObject={GlobalTopFilterObject}
              GlobalActiveFilters={GlobalActiveFilters}
              IsItemVisibleFunc={filter.isItemVisibleFunc}
              allActiveFilters={allActiveFilters}
              setAllActiveFilters={setAllActiveFilters}
            />
          </span>
        ))}
      </div>

      <FDFilterDropdownFooter
        isDisabled={isLimitError}
        isLimitError={isLimitError}
        onApply={() => {
          setApply(true);
          toggleFiltersNav();
        }}
        onCancel={() => {
          toggleFiltersNav();
          dispatch({ type: globalFilterTypes.SET_ACTIVE_FILTERS, payload: null });
          setOpenAccordions({});
        }}
      />
    </div>
  );

  return (
    <Popover
      noCloseOnScroll
      isOpen={isOpen}
      onClose={() => {
        setOpen(false);
        setOpenAccordions({});
        dispatch({
          type: globalFilterTypes.SET_ACTIVE_FILTERS,
          payload: null,
        });
      }}
      button={
        <EuiFilterButton
          className={
            selectedFilters === 0
              ? "euiFilterButton__notification_hidden filter_button"
              : "filter_button"
          }
          hasActiveFilters={selectedFilters > 0}
          isSelected={isOpen}
          numActiveFilters={selectedFilters}
          numFilters={1000}
          onClick={toggleFiltersNav}>
          <span style={{ position: "absolute", top: "5px" }}>
            <EuiIcon type="filter" />
          </span>
          <span style={{ paddingLeft: "20px" }}>Filters</span>
        </EuiFilterButton>
      }>
      {filtersComponent}
    </Popover>
  );
};

export default SideFilter;
